import { useMemo } from 'react';
import { toWireDate } from '@forecastify/shared';
import { DatePicker, type CalendarLabels } from '@forecastify/ui';
import { useT } from '../i18n';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Every date in the product, on the design system's own picker.             │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE PRODUCT SHIPPED TWENTY `<input type="date">`S AND THE DESIGN SYSTEM'S `DatePicker` WAS
 * USED BY NOTHING BUT THE SPECIMEN.** Each one was defensible when it was written — a native input
 * inherits the platform's keyboard, its mobile wheel and its assistive-technology support, which is
 * the same argument `select.tsx` makes and wins. It loses here for two reasons the select does not
 * face:
 *
 *  * **the mockup draws a calendar, not a control.** `2fazis.ContractDetail.html`, the task dialog
 *    and the period detail all use `Shadcn.DatePicker` with a month grid, a *Ma* button and a
 *    clear — a native input has none of those, and cannot be made to;
 *  * **a native date input renders in the BROWSER's locale, not the product's.** A Hungarian user
 *    on an en-US profile gets `mm/dd/yyyy` on a screen whose every other word is Hungarian, and
 *    the segment order changes under them between machines. That is not a styling difference.
 *
 * ⚠️ **One wrapper rather than twenty call sites**, because `DatePicker` needs `today` and a full
 * `CalendarLabels` on every use, and rule 7 means those are strings from the catalogue. Twenty
 * copies of that plumbing is twenty chances to hand it `Sz` twice.
 */

/**
 * ⚠️ **`Sze` and `Szo`, not `Sz` twice.** The design system's own `RangePicker` hardcodes `Sz` for
 * both *szerda* and *szombat*, which makes two columns of the week indistinguishable —
 * `design-system-findings.md` I-4. The catalogue holds the seven, so this cannot regress quietly.
 *
 * ⚠️ **Comma-separated in ONE key rather than twelve keys.** `i18n:check` reads literal arguments,
 * so `t(\`calendar.month.${n}\`)` would report all twelve as orphans and somebody would delete
 * them. A list that has to stay a list is one string.
 */
export function useCalendarLabels(): CalendarLabels {
  const t = useT();
  return useMemo(
    () => ({
      months: t('calendar.months').split(','),
      weekdays: t('calendar.weekdays').split(','),
      today: t('calendar.today'),
      clear: t('calendar.clear'),
      previousMonth: t('calendar.previousMonth'),
      previousPeriod: t('calendar.previousPeriod'),
      nextPeriod: t('calendar.nextPeriod'),
      zoomOut: t('calendar.zoomOut'),
      nextMonth: t('calendar.nextMonth'),
      placeholder: t('calendar.placeholder'),
      dialogLabel: t('calendar.dialogLabel'),
    }),
    [t],
  );
}

/**
 * A wire date, picked from a calendar.
 *
 * ⚠️ **`''` and `null` both mean "no date", and the caller keeps whichever it had.** Half the
 * product holds an unset date as `''` (a form draft, because that is what an input gives) and half
 * as `null` (a wire value). Normalising here rather than at twenty call sites is what makes this a
 * drop-in replacement for the input it removes.
 */
export function DateField({
  value,
  onChange,
  clearable = true,
  disabled = false,
  label,
  open,
  onOpenChange,
  className,
  buttonClassName,
}: {
  readonly value: string | null | undefined;
  readonly onChange: (value: string) => void;
  /** A start date that must exist is not clearable — the contract's, the recurrence's. */
  readonly clearable?: boolean;
  readonly disabled?: boolean;
  /** Names the trigger for a screen reader — a calendar button reading only a date names nothing. */
  readonly label: string;
  /* ⚠️ Controlled disclosure — only the time machine's kebab uses it; see `DatePicker`. */
  readonly open?: boolean;
  readonly onOpenChange?: (next: boolean) => void;
  readonly className?: string;
  readonly buttonClassName?: string;
}) {
  const labels = useCalendarLabels();

  /**
   * ⚠️ **`today` is computed here, not frozen.** The specimen pins it to the mockup's date so the
   * screenshots match; a real screen must ring the actual today, and `toWireDate` is the one
   * converter that turns an instant into a calendar date without a timezone shift — see the driver
   * rows in `hard-won.md` on why there is exactly one.
   */
  const today = useMemo(() => toWireDate(new Date()), []);

  if (disabled) {
    return (
      <span className="text-muted-foreground pl-2 text-[11.5px] tabular-nums">
        {value === null || value === undefined || value === '' ? '–' : value}
      </span>
    );
  }

  return (
    <DatePicker
      value={value === '' ? null : value}
      today={today}
      labels={labels}
      clearable={clearable}
      onChange={(next) => {
        onChange(next ?? '');
      }}
      {...(open === undefined || onOpenChange === undefined ? {} : { open, onOpenChange })}
      {...(className === undefined ? {} : { className })}
      {...(buttonClassName === undefined ? {} : { buttonClassName })}
      ariaLabel={label}
    />
  );
}
